import { formatNumber } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  GetForwardPointsResponse,
  LegPrice,
  MarketRates,
  MultiHedge,
  QuotePair
} from '@approot/models';
import {
  ConfigService,
  HedgePayload,
  HedgeQuoteMessage,
  LegService,
  ProductsConfigService,
  RfqTabService,
  SetupEventChange,
  VolBasedRfqOptionPricePayload
} from '@approot/services';
import {
  camelToKebabCase,
  clearEmptyValues,
  clone,
  DateUtilities,
  isFeatureEnabled,
  isValidDate,
  reformatDate,
  sleep,
  SubscriptionSink
} from '@approot/utilities';
import { RfqSetupService } from '@approot/widgets/rfq/rfq-container/rfq-tab/rfq-setup/rfq-setup.service';
import { isEqual } from 'lodash';
import { firstValueFrom, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  tap
} from 'rxjs/operators';
import {
  HedgeExecutionMode,
  hedgeRatesValidator,
  LiveHedgeRate
} from '../rfq-setup/rfq-setup';
import { RfqTabState, RfqTabStateService } from '../rfq-tab-state.service';

type Type = 'Spot' | 'Forward' | 'NDF';
const hedgeTypesIfMedusaDeliverable: Type[] = ['Spot', 'Forward'];
const hedgeTypesIfMedusaNonDeliverable: Type[] = ['NDF'];

type DateField = 'settlementDate' | 'expirationDate';
type NumberField = 'amount' | 'rate';

@Component({
  selector: 'app-rfq-multi-hedge',
  templateUrl: './rfq-multi-hedge.component.html',
  styleUrls: ['./rfq-multi-hedge.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RfqMultiHedgeComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Output() public submitSetupEvent = new EventEmitter<SetupEventChange>();
  @Output() public changeInHedgeType = new EventEmitter<{
    type: Type;
    index: number;
  }>();
  @Output() public onDelete = new EventEmitter<void>();
  @Output() public onChangeInExecutionMode =
    new EventEmitter<HedgeExecutionMode>();
  @Output() public onChangeInSpotRef = new EventEmitter<{
    key: string;
    value: string;
  }>();
  @Input() isPreset = false;
  @Input() public formData: Partial<MultiHedge> = {};
  @Input() public currencyPair: QuotePair;
  @Input({ required: true }) public premiumCcy = '???';
  @Input() public premiumDate: string;
  @Input() public legSettlementDate: string;
  @Input() public firstLegExpiryDate: string;
  @Input() public index: number;
  @Input() public counterparty = 'Sales Desk';
  @Input() public hedgeMode = '';
  @Input() public numOfHedges: number;
  @Input() public blockVolBasedPriceHedgeChanges = false;
  @Input() public isGenericMultiHedge = false;
  @Input() public pipsHighlightedIndices: number[] = [];
  @Input() public isSettlementDatesSame = false;
  @Input() public executionMode: HedgeExecutionMode;
  @Input() public hedgeRateValues: LiveHedgeRate;
  @Input() public showGenericMultiHedgeFields = false;
  @Input() public marketRates: MarketRates;
  @Input() public hedgeQuote: HedgeQuoteMessage;
  @Input() public hedge: string;
  public form: FormGroup;
  public hedgeTypes = hedgeTypesIfMedusaDeliverable;
  public type: Type = 'Spot';
  public settlementDateReadOnly = true;
  public showImpliedSpot = false;
  public rfqTabState: RfqTabState = {};
  public rfqRunning = true;
  public executionsModes: HedgeExecutionMode[];
  public spotRefSettings = defaultHedgeSettings();
  public fwdPointsSettings = defaultHedgeSettings();
  public fwdRateSettings = defaultHedgeSettings();
  public isSpotRefMethodStream = false;
  public marketDataSwapPoint: { ask: string; bid: string };
  public marketDataFwdRate: { ask: string; bid: string; mid: string };
  private defaultHedgeType: Type = 'Spot';
  private subscriptions = new SubscriptionSink();
  private defaultSettlementDate: string;
  private defaultExpirationDate: string;
  private changeInHedgeClone: MultiHedge;
  private isElementFocused = false;

  public constructor(
    public rfqTabService: RfqTabService,
    public changeRef: ChangeDetectorRef,
    private legService: LegService,
    private renderer: Renderer2,
    private elemRef: ElementRef,
    private productsConfigService: ProductsConfigService,
    private rfqState: RfqTabStateService,
    private rfqSetupService: RfqSetupService,
    private configService: ConfigService
  ) {
    effect(_ => {
      this.rfqRunning = this.rfqTabService.rfqRunning();
    });

    if (this.rfqTabService.executionContextType() === 'genericMultiHedge') {
      effect(_ => {
        this.updateAmountFromPricing(this.rfqTabService.pricingResult());
      });
    }
  }

  public ngAfterViewInit(): void {
    if (
      this.rfqTabService.executionContextType() === 'genericMultiHedge' &&
      this.index === this.numOfHedges - 1 &&
      this.form.get('executionMode')
    ) {
      setTimeout(() => {
        this.onChangeInExecutionMode.emit(this.form.get('executionMode').value);
      }, 0);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const premiumCcyChange = changes.premiumCcy;
    if (premiumCcyChange?.currentValue && !premiumCcyChange.firstChange) {
      if (this.form?.get('productType').value === 'Spot') {
        this.onProductTypeSpotChange();
      }
      this.form.get('amount').setValue('');
    }
    if (changes.hedgeMode?.currentValue) {
      this.maybeDisableHedgeRate();
    }

    if (changes.executionMode?.currentValue && this.index !== 0) {
      this.form?.get('executionMode')?.setValue(this.executionMode);
      this.executionModeChange(this.executionMode);
    }

    if (changes.hedgeRateValues?.currentValue !== undefined) {
      this.fwdPointsSettings.pending = false;
      this.spotRefSettings.pending = false;
      this.fwdRateSettings.pending = false;

      if (this.hedgeRateValues && this.form) {
        setTimeout(() => {
          Object.keys(this.hedgeRateValues).forEach(key => {
            const ctrl = this.form.get(key);
            if (ctrl?.enabled) {
              this.form.get(key)?.setValue(this.hedgeRateValues[key]);
            }
          });
          this.updateFwdRate();
        }, 0);
      }
    }

    if (changes.marketRates?.currentValue) {
      this.onMarketRatesChange();
    }

    if (changes.hedgeQuote?.currentValue) {
      this.updateAmountFromHedgeQuote();
    }
  }

  /**
   *
   */
  public async ngOnInit(): Promise<void> {
    this.form = this.createForm(this.formData);
    this.setupFormSubscriptions();
    this.initConfig();
    this.rfqTabService.registerNewVolBasedQuoteListener(
      this.quoteUpdateListener.bind(this)
    );
    this.rfqState.stateChanged
      .pipe(distinctUntilChanged((a, b) => isEqual(a, b)))
      .subscribe(state => {
        this.rfqTabState = state;
        this.maybeDisableHedgeRate();
        this.changeRef.markForCheck();
      });
  }

  /**
   *
   */
  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   *
   */
  public get valid(): boolean {
    return this.form.valid;
  }

  /**
   * Tell the group wrapper that this hedge is to be deleted.
   * The wrapper will then remove this component (via *ngIf)
   */
  public delete(): void {
    if (this.rfqRunning || this.numOfHedges === 1 || this.isGenericMultiHedge) {
      return;
    }
    this.onDelete.emit();
  }

  /**
   * The form's value, ensuring that the date fields are correct. Some values are
   * purged on certain conditions - see below.
   */
  public value(): MultiHedge {
    const value: MultiHedge = this.form.getRawValue();
    if (value.settlementDate) {
      value.settlementDate = reformatDate(value.settlementDate, 'YYYY-MM-DD');
    }

    if (value.expirationDate) {
      value.expirationDate = reformatDate(value.expirationDate, 'YYYY-MM-DD');
    }

    // It's important that we don't return any values "auto-filled" from the backend
    // after the 1st time the quote is updated, only ones we entered manually.
    if (!this.isFieldManual('amount')) {
      delete value.amount;
    }
    if (!this.isFieldManual('rate')) {
      delete value.rate;
    }
    return clearEmptyValues<MultiHedge>(value);
  }

  /**
   *
   */
  public async amountChange(): Promise<void> {
    const amount = ((this.form.get('amount').value || '') as string).trim();
    this.markFieldAsChanged('amount', !!amount);
    await sleep(5);
    this.isFieldManual('amount');
  }

  /**
   *
   */
  public async dateFieldChange(field: DateField): Promise<void> {
    await this.calculateFutureDate(field);
    this.markFieldAsChanged(field);
  }

  /**
   *
   */
  public rateChangeKBEvent(event: KeyboardEvent): void {
    const keysToIgnore = ['ArrowLeft', 'ArrowRight', 'Enter'];

    if (keysToIgnore.includes(event.code)) {
      return;
    }

    const val = this.form.get('rate').value;
    this.markFieldAsChanged('rate', val !== '');
  }

  /**
   *
   */
  public onElemFocus() {
    this.isElementFocused = true;
  }

  /**
   *
   */
  public async onCalendarChange(field: string) {
    await sleep(100);
    if (isValidDate(this.form.get(field).value, 'DD MMM YYYY')) {
      this.onElemBlur();
    }
  }

  /**
   *
   */
  public async onElemBlur() {
    this.isElementFocused = false;
    setTimeout(_ => {
      if (!this.isElementFocused) {
        this.emitChangeInHedges();
      }
    }, 0);
  }

  /********************** PRIVATE **********************/

  /**
   *
   */
  private maybeDisableHedgeRate() {
    let disabled = false;
    if (!this.form) {
      return;
    }
    const ctrl = this.form.get('rate');
    if (this.hedgeMode === 'Live') {
      this.markFieldAsChanged('rate', false);
      if (this.rfqTabState.rfqStatus === 'Traded') {
        disabled = false;
      } else {
        disabled = true;
      }
    } else {
      disabled = false;
    }
    if (disabled) {
      ctrl.disable({ emitEvent: false });
    } else {
      ctrl.enable({ emitEvent: false });
    }
  }

  /**
   *
   */
  private async emitChangeInHedges() {
    const settlementDate = this.form.value.settlementDate;
    if (
      this.changeInHedgeValidation &&
      this.form.valid &&
      (!settlementDate || isValidDate(settlementDate, 'D MMM YYYY'))
    ) {
      this.changeInHedgeClone = clone(this.form.value);
      if (this.rfqTabService.executionContextType() !== 'genericMultiHedge') {
        this.rfqTabService.setupMultiHedgesValidating = true;
      }
      this.rfqTabService.changeInHedges.next();
    }
  }

  /**
   *
   */
  private get changeInHedgeValidation(): boolean {
    return !isEqual(this.form.value, this.changeInHedgeClone);
  }

  /**
   *
   */
  private setImpliedSpot(): boolean {
    if (this.hedgeMode !== 'DX') {
      return false;
    }
    const formValue = this.form.value;
    if (formValue.productType !== 'Forward') {
      return false;
    }
    if (formValue.rate === '') {
      return false;
    }
    return true;
  }

  /**
   * Callback for when the quote is updated.
   */
  private quoteUpdateListener(
    event: VolBasedRfqOptionPricePayload | { hedges: HedgePayload[] }
  ): void {
    if (this.blockVolBasedPriceHedgeChanges) {
      return;
    }
    const hedges = event.hedges;
    const hedge = hedges[this.index];
    const ccyPair = this.rfqTabService.getCurrencyPair();
    const dp = this.productsConfigService.decimalPlacesForCross(ccyPair);

    this.form.get('offerDirection').setValue(hedge.position);

    let impliedSpot = hedge.impliedSpot;
    const impliedSpotCtrl = this.form.get('impliedSpot');
    if (impliedSpot != null) {
      impliedSpotCtrl.setValue(impliedSpot);
    } else {
      impliedSpotCtrl.setValue('');
    }

    const amountCtrl = this.form.get('amount');
    if (hedge.notional != null) {
      const amount = (+hedge.notional).toFixed(0);
      amountCtrl.setValue(amount);
    } else {
      this.markFieldAsChanged('amount', true);
      amountCtrl.setValue('');
    }

    const rateCtrl = this.form.get('rate');
    if (hedge.strike == null) {
      this.markFieldAsChanged('rate', true);
      rateCtrl.setValue('', { emitEvent: false });
    } else {
      let isManual = false;
      if (rateCtrl.value === '') {
        isManual = false;
        this.markFieldAsChanged('rate', false);
      } else {
        isManual = this.isFieldManual('rate');
      }
      if (!isManual && (rateCtrl.value === '' || !this.hasFocus('rate'))) {
        const rate = (+hedge.strike).toFixed(dp);
        rateCtrl.setValue(rate, { emitEvent: false });
        this.markFieldAsChanged('rate', false);
      }
    }

    this.form.updateValueAndValidity();
    this.changeRef.markForCheck();
    this.changeInHedgeClone = clone(this.form.value);
  }

  /**
   *
   */
  private async initConfig(): Promise<void> {
    this.setHedgeTypes();
    this.setHedgeRates();
    this.executionsModes = ['Live - Forward Ref.', 'DX - Forward Ref.'];

    if (isFeatureEnabled('enableSpotExecutionModes', false)) {
      this.executionsModes.splice(1, 0, 'Live - Spot + Pts Ref.');
      this.executionsModes.splice(2, 0, 'Spot DX with Live pts');
    }

    if (this.form.get('executionMode')) {
      this.executionModeChange(this.form.get('executionMode').value);
      if (this.rfqTabService.setupData.spotRefMethod === 'stream') {
        this.fwdRateSettings.readonly = true;
        this.fwdPointsSettings.readonly = true;
        this.spotRefSettings.readonly = true;
      }
    }

    let productType =
      (this.rfqTabService.tabData.isVolBased && !this.isPreset) ||
      this.rfqTabService.executionContextType() === 'genericMultiHedge'
        ? 'Forward'
        : this.formData.productType;

    if (productType === 'None') {
      return;
    }

    this.defaultHedgeType = this.hedgeTypes.includes(productType)
      ? productType
      : 'NDF';
    this.form.get('productType').setValue(this.defaultHedgeType, {
      emitEvent: hedgeTypesIfMedusaDeliverable.find(
        hedgeType => hedgeType === productType
      )
    });
  }

  private setHedgeTypes(): void {
    if (this.rfqTabService.executionContextType() === 'genericMultiHedge') {
      this.hedgeTypes = ['Forward'];
    } else if (this.rfqTabService.isMedusaDeliverable()) {
      this.hedgeTypes = hedgeTypesIfMedusaDeliverable;
    } else {
      this.hedgeTypes = hedgeTypesIfMedusaNonDeliverable;
    }
  }

  private setHedgeRates(): void {
    if (
      this.form.get('hedgeRateAsk')?.enabled &&
      this.form.get('hedgeRateBid')?.enabled &&
      this.firstLegExpiryDate
    ) {
      const cross = `${this.currencyPair.base}${this.currencyPair.term}`;
      this.rfqSetupService
        .getLatestFwdRef([this.firstLegExpiryDate], cross)
        .subscribe({
          next: (response: GetForwardPointsResponse) => {
            const dp = +this.productsConfigService.decimalPlacesForCross(cross);
            const forwards =
              response.forwards[
                DateUtilities.prettyDateToDate(this.firstLegExpiryDate)
              ].outrightForward;
            this.form.get('hedgeRateAsk').setValue(forwards.ask.toFixed(dp));
            this.form.get('hedgeRateBid').setValue(forwards.bid.toFixed(dp));
          }
        });
    }
  }
  /**
   *
   */
  private createForm(formData: any): FormGroup {
    const d = formData || {};

    this.type = d.productType || this.defaultHedgeType;

    const premiumDate = reformatDate(
      d.premiumDate || this.premiumDate,
      'D/M/YYYY'
    );

    const settlementDate = getFormattedDate(d.settlementDate, premiumDate);
    const expirationDate = getFormattedDate(d.expirationDate, '');

    this.defaultSettlementDate = settlementDate;
    this.defaultExpirationDate = expirationDate;

    // bidDirection is always the opposite of offerDirection so it's just indicative.
    // It's only a form element at all to keep the L&F more consistent.
    const bidDirection = new FormControl(
      d.bidDirection || 'Sell',
      Validators.required
    );
    bidDirection.disable();

    const offerDirection = new FormControl(
      d.offerDirection || 'Buy',
      Validators.required
    );

    let ctrls: Record<string, FormControl | FormGroup> = {
      productType: new FormControl(this.type, Validators.required),
      amount: new FormControl(d.amount || ''),
      offerDirection,
      bidDirection,
      rate: new FormControl(d.rate || ''),
      expirationDate: new FormControl(expirationDate),
      settlementDate: new FormControl(settlementDate),
      impliedSpot: new FormControl(d.impliedSpot || '')
    };

    if (this.showGenericMultiHedgeFields) {
      ctrls = {
        ...ctrls,
        direction: new FormControl(''),
        executionMode: new FormControl(
          'Live - Forward Ref.',
          Validators.required
        )
      };

      const spotReg = /^\d+(\.\d+)?$/;
      [
        'spotRefAsk',
        'spotRefMid',
        'spotRefBid',
        'fwdRateAsk',
        'fwdRateMid',
        'fwdRateBid',
        'fwdPointAsk',
        'fwdPointMid',
        'fwdPointBid'
      ].forEach(field => {
        ctrls[field] = new FormControl('', [
          Validators.required,
          Validators.pattern(spotReg)
        ]);
      });
    }

    const fg = new FormGroup(ctrls);

    if (this.showGenericMultiHedgeFields) {
      [
        {
          ask: 'spotRefAsk',
          bid: 'spotRefBid'
        },
        {
          ask: 'fwdRateAsk',
          bid: 'fwdRateBid'
        },
        {
          ask: 'fwdPointAsk',
          bid: 'fwdPointBid'
        }
      ].forEach(fields => {
        fg.addValidators(hedgeRatesValidator.bind(this, fields));
      });
    }

    this.markFieldAsChanged('amount', false);
    this.maybeDisableHedgeRate();
    return fg;
  }

  /**
   *
   */
  private async setDate(
    field: DateField,
    date: string,
    force = true
  ): Promise<void> {
    const dateCtrl = this.form.get(field);
    if (dateCtrl.value && !force) {
      return;
    }
    // Without a delay, the field doesn't get set (no idea why)
    await sleep(100);
    const dateStr = reformatDate(date, 'D/M/YYYY');
    this.form.get(field).setValue(dateStr, { emitEvent: true });
  }

  /**
   *
   */
  private setupFormSubscriptions(): void {
    this.subscriptions.sink = this.form.valueChanges
      .pipe(distinctUntilChanged((a, b) => !isEqual(a, b)))
      .subscribe({
        next: async () => {
          await sleep(50);
          this.form.updateValueAndValidity();
          this.changeRef.markForCheck();
        }
      });

    this.subscriptions.sink = this.form
      .get('offerDirection')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe({
        next: offer => {
          const bid = offer === 'Buy' ? 'Sell' : 'Buy';
          this.form.get('bidDirection').setValue(bid, { emitEvent: false });
          this.rfqTabService.handleClientQuoteEventUsingLast('offerDirection');
        }
      });

    this.subscriptions.sink = this.form
      .get('executionMode')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe({
        next: val => {
          this.executionModeChange(val);
        }
      });

    this.subscriptions.sink = this.form
      .get('productType')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe({
        next: async type => {
          this.onProductTypeChange(type);
        }
      });

    this.subscriptions.sink = this.form
      .get('settlementDate')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe(v => this.checkIfSettlementDateChanged(v));

    this.subscriptions.sink = this.form
      .get('expirationDate')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe(v => this.checkIfExpirationDateChanged(v));

    this.subscriptions.sink = this.form
      .get('rate')
      .valueChanges.pipe(distinctUntilChanged(), debounceTime(250))
      .subscribe(_ => {
        this.rfqTabService.handleClientQuoteEventUsingLast('rate');
      });

    ['spotRefAsk', 'spotRefBid', 'spotRefMid'].forEach(field => {
      this.subscriptions.sink = this.form
        .get(field)
        ?.valueChanges.pipe(distinctUntilChanged())
        .subscribe({
          next: val => {
            if (this.index === 0) {
              this.onChangeInSpotRef.emit({ key: field, value: val });
            }

            this.updateFwdRate();
          }
        });
    });

    ['fwdRateBid', 'fwdRateAsk', 'fwdRateMid'].forEach(field => {
      this.subscriptions.sink = this.form
        .get(field)
        ?.valueChanges.pipe(distinctUntilChanged())
        .subscribe({
          next: _ => {
            if (
              !this.fwdRateSettings.readonly ||
              this.rfqTabService.setupData.spotRefMethod === 'stream'
            ) {
              this.submitSetupEvent.emit('hedgeRate');
            }
            this.updateQuotesOutdatedStatus();
          }
        });
    });

    ['fwdPointAsk', 'fwdPointBid'].forEach(field => {
      this.subscriptions.sink = this.form
        .get(field)
        ?.valueChanges.pipe(distinctUntilChanged())
        .subscribe({
          next: _ => {
            this.updateFwdRate();
          }
        });
    });

    this.subscriptions.sink = this.form.valueChanges.subscribe(() => {
      this.showImpliedSpot = this.setImpliedSpot();
    });
  }

  private updateQuotesOutdatedStatus(): void {
    if (this.executionMode === 'DX - Forward Ref.') {
      this.rfqTabService.updateQuotesOutdatedStatus();
    }
  }

  private updateFwdRate(): void {
    if (!this.form) {
      return;
    }
    if (this.executionMode === 'Live - Spot + Pts Ref.') {
      this.updateFwdRatePtsRef();
    }

    if (this.executionMode === 'Spot DX with Live pts') {
      this.updateFwdRateLivePts();
    }

    this.updateQuotesOutdatedStatus();
  }

  private updateFwdRatePtsRef(): void {
    const cross = `${this.currencyPair.base}${this.currencyPair.term}`;
    const dp = this.productsConfigService.decimalPlacesForCross(cross);

    if (
      this.form.get('spotRefAsk').value &&
      this.form.get('fwdPointAsk').value
    ) {
      const spotRefAsk = +this.form.get('spotRefAsk').value;
      const fwdPointAsk = +this.form.get('fwdPointAsk').value;
      const fwdRateAsk =
        spotRefAsk + fwdPointAsk / this.rfqTabService.forwardPointsMultiplier();
      this.form
        .get('fwdRateAsk')
        .setValue(isNaN(fwdRateAsk) ? '' : fwdRateAsk.toFixed(dp));
    } else {
      this.form.get('fwdRateAsk').setValue('');
    }

    if (
      this.form.get('spotRefBid').value &&
      this.form.get('fwdPointBid').value
    ) {
      const spotRefBid = +this.form.get('spotRefBid').value;
      const fwdPointBid = +this.form.get('fwdPointBid').value;
      const fwdRateBid =
        spotRefBid + fwdPointBid / this.rfqTabService.forwardPointsMultiplier();
      this.form
        .get('fwdRateBid')
        .setValue(isNaN(fwdRateBid) ? '' : fwdRateBid.toFixed(dp));
    } else {
      this.form.get('fwdRateBid').setValue('');
    }
    this.submitSetupEvent.emit('hedgeRate');
  }

  private updateFwdRateLivePts(): void {
    if (!this.form.get('spotRefMid').value) {
      this.form.get('fwdRateBid').setValue('');
      this.form.get('fwdRateAsk').setValue('');
      return;
    }
    const cross = `${this.currencyPair.base}${this.currencyPair.term}`;
    const dp = this.productsConfigService.decimalPlacesForCross(cross);
    const spotRefMid = +this.form.get('spotRefMid').value;

    if (this.form.get('fwdPointAsk').value) {
      const fwdPointAsk = +this.form.get('fwdPointAsk').value;
      const fwdRateAsk =
        spotRefMid + fwdPointAsk / this.rfqTabService.forwardPointsMultiplier();
      this.form
        .get('fwdRateAsk')
        .setValue(isNaN(fwdRateAsk) ? '' : fwdRateAsk.toFixed(dp));
    } else {
      this.form.get('fwdRateAsk').setValue('');
    }

    if (this.form.get('fwdPointBid').value) {
      const fwdPointBid = +this.form.get('fwdPointBid').value;
      const fwdRateBid =
        spotRefMid + fwdPointBid / this.rfqTabService.forwardPointsMultiplier();
      this.form
        .get('fwdRateBid')
        .setValue(isNaN(fwdRateBid) ? '' : fwdRateBid.toFixed(dp));
    } else {
      this.form.get('fwdRateBid').setValue('');
    }
    this.submitSetupEvent.emit('hedgeRate');
  }

  private onProductTypeChange(type: Type): void {
    this.type = type;
    this.settlementDateReadOnly = true;
    const ctrl = this.form.get('settlementDate');
    switch (type) {
      case 'Spot':
        this.onProductTypeSpotChange();
        break;
      case 'Forward':
      case 'NDF':
        this.defaultSettlementDate = getFormattedDate(
          this.formData.settlementDate,
          this.legSettlementDate
        );
        this.setDate('settlementDate', this.defaultSettlementDate);
        this.setDate('expirationDate', this.defaultExpirationDate);
        this.settlementDateReadOnly = false;
        ctrl.enable();
        break;
      default:
        this.settlementDateReadOnly = false;
        ctrl.enable();
        break;
    }
    if (this.isGenericMultiHedge) {
      this.settlementDateReadOnly = true;
      ctrl.disable();
    }
    this.checkIfExpirationDateChanged();
    this.checkIfSettlementDateChanged();
    this.rfqTabService.handleClientQuoteEventUsingLast('productType');

    if (this.rfqTabService.isMedusaDeliverable()) {
      this.changeInHedgeType.emit({ type: this.type, index: this.index });
    }
  }

  private onProductTypeSpotChange(): void {
    const ctrl = this.form.get('settlementDate');
    this.defaultSettlementDate = getFormattedDate(this.premiumDate, '');
    ctrl.markAsPending();
    this.rfqTabService.calculateSpotDate().subscribe({
      next: formattedDate => this.setDate('settlementDate', formattedDate),
      error: () => ctrl.setValue('')
    });
    ctrl.disable();
  }

  /**
   *
   */
  private checkIfSettlementDateChanged(val?: string): void {
    this.checkIfDateFieldChanged(
      'settlementDate',
      this.defaultSettlementDate,
      val
    );
  }

  /**
   *
   */
  private checkIfExpirationDateChanged(val?: string): void {
    this.checkIfDateFieldChanged(
      'expirationDate',
      this.defaultExpirationDate,
      val
    );
  }

  /**
   *
   */
  private async checkIfDateFieldChanged(
    field: DateField,
    defaultVal: string,
    val?: string
  ): Promise<void> {
    await sleep(100);
    if (!val) {
      val = this.form.get(field).value;
    }
    const d1 = reformatDate(val, 'YYYY-MM-DD');
    const d2 = reformatDate(defaultVal, 'YYYY-MM-DD');
    this.markFieldAsChanged(field, d1 !== d2);
  }

  /**
   *
   */
  private async markFieldAsChanged(
    field: DateField | NumberField,
    changed = true
  ): Promise<void> {
    await sleep(50);
    const className = camelToKebabCase(field);
    const root = this.elemRef.nativeElement;
    const elem = root.querySelector(`.${className} input`);
    if (!elem) {
      return;
    }

    this.renderer.setProperty(elem, 'state', changed ? 'manual' : 'server');

    this.renderer.setStyle(
      elem,
      'color',
      changed ? 'var(--near-turquoise)' : 'white'
    );
  }

  private hasFocus(field: DateField | NumberField): boolean {
    const className = camelToKebabCase(field);
    const root = this.elemRef.nativeElement;
    const elem = root.querySelector(`.${className} input`);
    if (!elem) {
      return false;
    }
    return elem === document.activeElement;
  }

  private isFieldManual(field: DateField | NumberField): boolean {
    const className = camelToKebabCase(field);
    const root = this.elemRef.nativeElement;
    const elem = root.querySelector(`.${className} input`) as any;
    if (!elem) {
      return false;
    }

    const prop = elem['state'];
    return prop === 'manual';
  }

  /**
   * Get a valid settlement date from the backend,
   * including magic values like '1y' etc.
   */
  private async calculateFutureDate(field: DateField): Promise<void> {
    const ctrl = this.form.get(field);
    if (!ctrl.valid) {
      return;
    }
    const dateOrLag = reformatDate(ctrl.value, 'YYYY-MM-DD');
    const currencyPair = this.rfqTabService.getCurrencyPair();
    // calculateFutureDate is Promise<Observable<any>>
    ctrl.markAsPending();
    const obs = await this.legService.dateService({
      task: 'settlementDate',
      dateOrLag,
      currencyPair
    });
    return firstValueFrom(
      obs.pipe(
        filter(data => !!data?.[field]),
        tap(data => {
          const date = reformatDate(data[field], 'D MMM YYYY');
          ctrl.setValue(date, { emitEvent: false });
          this.emitChangeInHedges();
        }),
        catchError(x => {
          ctrl.updateValueAndValidity();
          return of(x);
        })
      )
    );
  }

  private executionModeChange(val: HedgeExecutionMode): void {
    if (!this.form) {
      return;
    }

    if (this.index === 0) {
      this.onChangeInExecutionMode.emit(val);
    }

    this.resetHedgeRateSettings(val, true);
  }

  public resetHedgeRateSettings(
    val: HedgeExecutionMode,
    executionModeChange: boolean
  ): void {
    this.enableDisableFields(
      [
        'spotRefAsk',
        'spotRefBid',
        'spotRefMid',
        'fwdRateAsk',
        'fwdRateMid',
        'fwdRateBid',
        'fwdPointAsk',
        'fwdPointMid',
        'fwdPointBid'
      ],
      false,
      executionModeChange
    );
    switch (val) {
      case 'Live - Forward Ref.':
        this.fwdRateSettings.twoWay = true;
        this.fwdRateSettings.readonly = false;
        this.enableDisableFields(
          ['fwdRateAsk', 'fwdRateBid'],
          true,
          executionModeChange
        );
        break;
      case 'Live - Spot + Pts Ref.':
        this.spotRefSettings.readonly = this.index !== 0;
        this.fwdRateSettings.twoWay = true;
        this.spotRefSettings.twoWay = true;
        this.fwdPointsSettings.twoWay = true;
        this.fwdRateSettings.readonly = true;
        this.fwdPointsSettings.readonly = false;
        this.enableDisableFields(
          ['spotRefAsk', 'spotRefBid'],
          true,
          executionModeChange
        );
        this.enableDisableFields(
          ['fwdRateAsk', 'fwdRateBid'],
          true,
          executionModeChange
        );
        this.enableDisableFields(
          ['fwdPointAsk', 'fwdPointBid'],
          true,
          executionModeChange
        );
        break;
      case 'Spot DX with Live pts':
        this.spotRefSettings.readonly = this.index !== 0;
        this.spotRefSettings.twoWay = false;
        this.fwdRateSettings.readonly = true;
        this.fwdRateSettings.twoWay = true;
        this.fwdPointsSettings.twoWay = true;
        this.fwdPointsSettings.readonly = false;
        this.enableDisableFields(['spotRefMid'], true, executionModeChange);
        this.enableDisableFields(
          ['fwdRateAsk', 'fwdRateBid'],
          true,
          executionModeChange
        );
        this.enableDisableFields(
          ['fwdPointAsk', 'fwdPointBid'],
          true,
          executionModeChange
        );
        break;
      case 'DX - Forward Ref.':
        this.fwdRateSettings.twoWay = false;
        this.fwdRateSettings.readonly = false;
        this.enableDisableFields(['fwdRateMid'], true, executionModeChange);
        break;
      default:
        break;
    }

    this.isSpotRefMethodStream =
      this.rfqTabService.setupData.spotRefMethod === 'stream';
    if (this.isSpotRefMethodStream) {
      this.fwdRateSettings.readonly = true;
      this.fwdPointsSettings.readonly = true;
      this.spotRefSettings.readonly = true;
    }

    this.changeRef.detectChanges();
  }

  private enableDisableFields(
    fields: string[],
    enable: boolean,
    executionModeChange: boolean
  ): void {
    fields.forEach(field => {
      if (executionModeChange) {
        this.form.get(field).setValue('');
      }
      if (enable) {
        this.form.get(field).enable();
      } else {
        this.form.get(field).disable();
      }
    });
  }

  private updateAmountFromPricing(pricingResult: LegPrice): void {
    if (pricingResult && this.form.get('productType').value === 'Forward') {
      const baseCurrency =
        pricingResult.portfolioResult.pricing.forwardDeltasBySettlementDate?.[
          `${this.currencyPair.base}${this.currencyPair.term}`
        ][reformatDate(this.defaultSettlementDate, 'YYYY-MM-DD')].baseCurrency;
      if (!baseCurrency) {
        return;
      }
      const amount = formatNumber(Math.abs(baseCurrency), 'en-GB', '1.2-2');
      this.form.get('amount').setValue(amount);
      this.form.get('amount').markAsPristine();
      const identifier = this.configService
        .userSettings()
        .parent.identifier.toUpperCase();
      this.form
        .get('direction')
        .setValue(
          baseCurrency > 0
            ? `${identifier} Sells / Buys`
            : `${identifier} Buys / Sells`
        );
      this.changeRef.detectChanges();
    }
  }

  private onMarketRatesChange(): void {
    const settlementDate = reformatDate(
      this.form.get('settlementDate').value,
      'YYYY-MM-DD'
    );
    this.marketDataFwdRate = this.marketRates.forwardRates.find(
      forwardRate => forwardRate.deliveryDate === settlementDate
    );

    this.updateSpotRefFromMarketRates();

    if (
      this.form &&
      this.marketDataFwdRate &&
      this.rfqTabService.setupData.spotRefMethod === 'stream'
    ) {
      if (this.form.get('fwdRateAsk').enabled) {
        this.form
          .get('fwdRateAsk')
          .setValue(this.marketDataFwdRate.ask, { emitEvent: false });
      }

      if (this.form.get('fwdRateBid').enabled) {
        this.form
          .get('fwdRateBid')
          .setValue(this.marketDataFwdRate.bid, { emitEvent: false });
      }

      if (this.form.get('fwdRateMid').enabled) {
        this.form
          .get('fwdRateMid')
          .setValue(this.marketDataFwdRate.mid, { emitEvent: false });
      }
    }

    this.marketDataSwapPoint = this.marketRates.swapPoints.find(
      swapPoint => swapPoint.deliveryDate === settlementDate
    );
  }

  private updateAmountFromHedgeQuote(): void {
    if (
      this.isGenericMultiHedge &&
      this.hedge === 'Forward Hedge' &&
      this.form?.get('settlementDate').value
    ) {
      const settlementDate = reformatDate(
        this.form.get('settlementDate').value,
        'YYYY-MM-DD'
      );
      const hedgeQuote = this.hedgeQuote.hedges.find(
        quote => quote.hedgeDeliveryDate === settlementDate
      );
      if (hedgeQuote) {
        const amount = formatNumber(
          Math.abs(hedgeQuote.hedgeAmount),
          'en-GB',
          '1.2-2'
        );
        this.form.get('amount').setValue(amount);
      }
    }
  }

  private updateSpotRefFromMarketRates(): void {
    if (
      this.marketRates?.spotRates &&
      this.rfqTabService.setupData.spotRefMethod === 'stream'
    ) {
      const dp = this.productsConfigService.decimalPlacesForCross(
        this.rfqTabService.getCurrencyPair()
      );

      if (this.form.get('spotRefAsk')?.enabled) {
        this.form
          .get('spotRefAsk')
          .setValue(this.marketRates.spotRates.ask, { emitEvent: false });
      }

      if (this.form.get('spotRefBid')?.enabled) {
        this.form
          .get('spotRefBid')
          .setValue(this.marketRates.spotRates.bid, { emitEvent: false });
      }

      if (this.form.get('spotRefMid')?.enabled) {
        this.form
          .get('spotRefMid')
          .setValue(
            (
              (+this.marketRates.spotRates.ask +
                +this.marketRates.spotRates.bid) /
              2
            ).toFixed(dp),
            { emitEvent: false }
          );
      }
    }
  }
}

function getFormattedDate(date: string, def: string): string {
  return date ? reformatDate(date, 'D/M/YYYY') : def;
}

function defaultHedgeSettings(): {
  twoWay: boolean;
  readonly: boolean;
  pending: boolean;
} {
  return {
    twoWay: true,
    readonly: false,
    pending: false
  };
}
